import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useFieldAuth } from '../contexts/FieldContext';
import WaiverSectionEditor from '../components/waiver/WaiverSectionEditor';
import WaiverRequiredFieldsEditor from '../components/waiver/WaiverRequiredFieldsEditor';
import SignatureSettingsEditor from '../components/waiver/SignatureSettingsEditor';
import WaiverPreview from '../components/waiver/WaiverPreview';
import NotificationContainer from '../components/common/NotificationContainer';
import * as waiverService from '../services/waiverService';
import {
  WaiverSection,
  WaiverRequiredFields,
  SignatureSettings,
  WaiverTemplate,
  CreateWaiverTemplateRequest,
  UpdateWaiverTemplateRequest,
} from '../types/waiver';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: 24px;
  padding-top: 140px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text};
  font-size: 24px;
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: ${theme.colors.primary};
          color: white;
          border: none;
          &:hover { opacity: 0.9; }
        `;
      case 'danger':
        return `
          background: ${theme.colors.error};
          color: white;
          border: none;
          &:hover { opacity: 0.9; }
        `;
      default:
        return `
          background: transparent;
          color: ${theme.colors.text};
          border: 1px solid ${theme.colors.border};
          &:hover { background: ${theme.colors.surfaceHover}; }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const EditorPanel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 16px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
`;

const PreviewPanel = styled.div`
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 15px;
  margin: 0 0 10px 0;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const Label = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  margin-bottom: 5px;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  min-width: 100px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const AddSectionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 6px;
  margin-top: 10px;
`;

const AddSectionButton = styled.button`
  padding: 5px 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.15);
  border: 1px solid rgba(244, 67, 54, 0.4);
  color: #f44336;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const LockedBanner = styled.div`
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.4);
  color: #ffc107;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const generateId = () => Math.random().toString(36).substr(2, 9);

const defaultRequiredFields: WaiverRequiredFields = {
  fullName: true,
  address: { enabled: false, required: false },
  phone: { enabled: true, required: false },
  email: { enabled: true, required: true },
  emergencyContact: { enabled: true, required: true },
  idNumber: { enabled: false, required: false },
  idIssueDate: { enabled: false, required: false },
  dateOfBirth: { enabled: true, required: true },
};

const defaultSignatureSettings: SignatureSettings = {
  graphicalSignature: true,
  checkboxAgreement: false,
  requireBoth: false,
};

const WaiverBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isFieldAuthenticated } = useFieldAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  const [name, setName] = useState('Waiver Agreement');
  const [year, setYear] = useState(new Date().getFullYear());
  const [sections, setSections] = useState<WaiverSection[]>([]);
  const [requiredFields, setRequiredFields] = useState<WaiverRequiredFields>(defaultRequiredFields);
  const [signatureSettings, setSignatureSettings] = useState<SignatureSettings>(defaultSignatureSettings);
  const [parentalConsentAge, setParentalConsentAge] = useState(18);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'success' | 'error' | 'info';
    title: string;
    message: string;
  }>>([]);

  const addNotification = (type: 'success' | 'error' | 'info', title: string, message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setNotifications(prev => [...prev, { id, type, title, message }]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    if (!isFieldAuthenticated) {
      navigate('/field/login');
      return;
    }

    if (id) {
      loadTemplate(id);
    }
  }, [id, isFieldAuthenticated, navigate]);

  const loadTemplate = async (templateId: string) => {
    setLoading(true);
    setError(null);
    try {
      const template = await waiverService.getTemplate(templateId);
      setName(template.name);
      setYear(template.year);
      setSections(template.sections);
      setRequiredFields(template.requiredFields);
      setSignatureSettings(template.signatureSettings);
      setParentalConsentAge(template.parentalConsentAgeThreshold);
      setIsLocked(template.isLocked);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = (type: WaiverSection['type']) => {
    const newSection: WaiverSection = {
      id: generateId(),
      type,
      order: sections.length,
      content: '',
      items: type === 'bullet_list' || type === 'numbered_list' ? [''] : [],
      headingLevel: type === 'image' ? 2 : 1, // Default Medium size for images
    };
    setSections([...sections, newSection]);
  };

  const handleSectionChange = (index: number, updatedSection: WaiverSection) => {
    const newSections = [...sections];
    newSections[index] = updatedSection;
    setSections(newSections);
  };

  const handleSectionDelete = (index: number) => {
    const newSections = sections.filter((_, i) => i !== index);
    // Reorder
    newSections.forEach((section, i) => {
      section.order = i;
    });
    setSections(newSections);
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sections.length) return;

    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    newSections.forEach((section, i) => {
      section.order = i;
    });
    setSections(newSections);
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const newSections = [...sections];
      const [draggedSection] = newSections.splice(dragIndex, 1);
      newSections.splice(dragOverIndex, 0, draggedSection);
      newSections.forEach((section, i) => {
        section.order = i;
      });
      setSections(newSections);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (isLocked) {
      setError('This template is locked and cannot be edited. Create a new version instead.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const sectionsToSave = sections.map(({ id, ...rest }) => rest);

      if (id) {
        const request: UpdateWaiverTemplateRequest = {
          name,
          sections: sectionsToSave,
          requiredFields,
          signatureSettings,
          parentalConsentAgeThreshold: parentalConsentAge,
        };
        await waiverService.updateTemplate(id, request);
        addNotification('success', 'Success', 'Template saved successfully');
      } else {
        const request: CreateWaiverTemplateRequest = {
          name,
          year,
          sections: sectionsToSave,
          requiredFields,
          signatureSettings,
          parentalConsentAgeThreshold: parentalConsentAge,
        };
        const result = await waiverService.createTemplate(request);
        addNotification('success', 'Success', 'Template created successfully');
        // Navigate after short delay
        setTimeout(() => {
          navigate(`/field/waiver/builder/${result.templateId}`, { replace: true });
        }, 500);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>Loading...</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title>{id ? 'Edit Waiver Template' : 'Create Waiver Template'}</Title>
        <HeaderButtons>
          <Button onClick={() => navigate('/field/management?tab=templates')}>Cancel</Button>
          <Button $variant="primary" onClick={handleSave} disabled={saving || isLocked}>
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </HeaderButtons>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {isLocked && (
        <LockedBanner>
          <span>🔒</span>
          <div style={{ flex: 1 }}>
            <strong>This template is locked</strong>
            <p style={{ margin: '4px 0 0' }}>
              This template has signatures and cannot be edited.
            </p>
          </div>
          <Button onClick={() => setShowEditor(!showEditor)}>
            {showEditor ? 'Hide Details' : 'Show Details'}
          </Button>
        </LockedBanner>
      )}

      {isLocked && !showEditor ? (
        <WaiverPreview title={name} sections={sections} />
      ) : (
        <MainContent>
          <EditorPanel>
            <Section>
              <SectionTitle>Basic Information</SectionTitle>
              <Label>Template Name</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Liability Waiver 2025"
              />
              <Row>
                <div style={{ flex: 1 }}>
                  <Label>Year</Label>
                  <Select value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={!!id}>
                    {[...Array(5)].map((_, i) => {
                      const y = new Date().getFullYear() + i;
                      return <option key={y} value={y}>{y}</option>;
                    })}
                  </Select>
                </div>
                <div style={{ flex: 1 }}>
                  <Label>Parental Consent Age</Label>
                  <Select
                    value={parentalConsentAge}
                    onChange={(e) => setParentalConsentAge(Number(e.target.value))}
                  >
                    {[16, 17, 18, 19, 21].map((age) => (
                      <option key={age} value={age}>{age} years</option>
                    ))}
                  </Select>
                </div>
              </Row>
            </Section>

            <Section>
              <SectionTitle>Content Sections</SectionTitle>
              {sections.map((section, index) => (
                <WaiverSectionEditor
                  key={section.id}
                  section={section}
                  index={index}
                  onChange={(s) => handleSectionChange(index, s)}
                  onDelete={() => handleSectionDelete(index)}
                  onMoveUp={() => handleMoveSection(index, 'up')}
                  onMoveDown={() => handleMoveSection(index, 'down')}
                  isFirst={index === 0}
                  isLast={index === sections.length - 1}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  isDragging={dragIndex === index}
                  isDragOver={dragOverIndex === index}
                />
              ))}
              <AddSectionBar>
                <span style={{ color: '#888', fontSize: '13px', marginRight: '8px' }}>Add:</span>
                <AddSectionButton onClick={() => handleAddSection('heading')}>Heading</AddSectionButton>
                <AddSectionButton onClick={() => handleAddSection('rich_text')}>Text</AddSectionButton>
                <AddSectionButton onClick={() => handleAddSection('image')}>Image</AddSectionButton>
                <AddSectionButton onClick={() => handleAddSection('bullet_list')}>Bullet List</AddSectionButton>
                <AddSectionButton onClick={() => handleAddSection('numbered_list')}>Numbered List</AddSectionButton>
                <AddSectionButton onClick={() => handleAddSection('divider')}>Divider</AddSectionButton>
              </AddSectionBar>
            </Section>

            <Section>
              <SectionTitle>Form Fields</SectionTitle>
              <WaiverRequiredFieldsEditor
                fields={requiredFields}
                onChange={setRequiredFields}
              />
            </Section>

            <Section>
              <SectionTitle>Signature Options</SectionTitle>
              <SignatureSettingsEditor
                settings={signatureSettings}
                onChange={setSignatureSettings}
              />
            </Section>
          </EditorPanel>

          <PreviewPanel>
            <WaiverPreview title={name} sections={sections} compact />
          </PreviewPanel>
        </MainContent>
      )}

      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
    </PageContainer>
  );
};

export default WaiverBuilderPage;
